/* Shared canonical 4-stage intake editor.
 * Used by vendor portal + admin manager.
 *
 * Renders the 4 fixed-kind stages and exposes per-stage config:
 *  - Stage 1 (consumer_activated): read-only narrative
 *  - Stage 2 (vendor_notified): notification prefs
 *  - Stage 3 (vendor_engages): asset list (forms / consents / scheduling)
 *  - Stage 4 (service_rendering): asset list + SLA + delivery notes
 */
import { Plus, Trash2, Mail, Phone, AppWindow, FileText, AlertCircle } from "lucide-react";

const STAGE_META = {
  consumer_activated: { color: "bg-slate-100 text-slate-700 border-slate-200", num: 1 },
  vendor_notified:    { color: "bg-blue-50 text-blue-800 border-blue-200",    num: 2 },
  vendor_engages:     { color: "bg-emerald-50 text-emerald-800 border-emerald-200", num: 3 },
  service_rendering:  { color: "bg-amber-50 text-amber-800 border-amber-200", num: 4 },
};

const ASSET_KINDS = [
  { v: "form",             label: "Form" },
  { v: "consent_doc",      label: "Consent doc" },
  { v: "schedule_link",    label: "Scheduling link" },
  { v: "payment_auth",     label: "Payment auth" },
  { v: "checklist_item",   label: "Checklist item" },
  { v: "message_template", label: "Message template" },
  { v: "other",            label: "Other" },
];

const newAsset = () => ({
  id: Math.random().toString(16).slice(2, 8),
  kind: "form",
  label: "",
  description: "",
  url: "",
  body: "",
  required: true,
});

export function VendorIntakeStageEditor({ stages, onChange, readOnly = false }) {
  if (!stages || stages.length === 0) return null;

  const updateStage = (idx, patch) => {
    const next = stages.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange(next);
  };
  const updateAsset = (stageIdx, assetIdx, patch) => {
    const stage = stages[stageIdx];
    const assets = (stage.assets || []).map((a, i) => (i === assetIdx ? { ...a, ...patch } : a));
    updateStage(stageIdx, { assets });
  };
  const removeAsset = (stageIdx, assetIdx) =>
    updateStage(stageIdx, { assets: (stages[stageIdx].assets || []).filter((_, i) => i !== assetIdx) });
  const addAsset = (stageIdx) =>
    updateStage(stageIdx, { assets: [...(stages[stageIdx].assets || []), newAsset()] });

  return (
    <div className="space-y-3" data-testid="vendor-intake-stage-editor">
      {stages.map((s, idx) => {
        const meta = STAGE_META[s.kind] || STAGE_META.consumer_activated;
        return (
          <div key={s.kind} data-testid={`intake-stage-${s.kind}`} className={`rounded-2xl border bg-white p-4`}>
            <div className="flex items-start gap-3 mb-3">
              <div className={`rounded-full ${meta.color} text-[11px] font-bold w-7 h-7 flex items-center justify-center flex-shrink-0 border`}>
                {meta.num}
              </div>
              <div className="flex-1">
                <div className="font-display text-base font-bold text-slate-900">{s.title}</div>
                {s.description && <div className="text-[12px] text-slate-600 mt-0.5">{s.description}</div>}
              </div>
            </div>

            {s.kind === "consumer_activated" && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <div>
                  Owned by Nodera + the clinic. Vendors don't configure this stage — it represents the bare-minimum PHI hand-off (name, contact, program) that triggers your intake.
                </div>
              </div>
            )}

            {s.kind === "vendor_notified" && (
              <div className="grid sm:grid-cols-3 gap-2" data-testid={`intake-notify-${s.kind}`}>
                <Toggle
                  icon={Mail}
                  label="Email"
                  checked={s.notify_email}
                  onChange={(v) => updateStage(idx, { notify_email: v })}
                  disabled={readOnly}
                  testId={`intake-notify-email-${idx}`}
                />
                <Toggle
                  icon={Phone}
                  label="SMS"
                  checked={s.notify_sms}
                  onChange={(v) => updateStage(idx, { notify_sms: v })}
                  disabled={readOnly}
                  testId={`intake-notify-sms-${idx}`}
                />
                <Toggle
                  icon={AppWindow}
                  label="In-portal"
                  checked={s.notify_in_portal}
                  onChange={(v) => updateStage(idx, { notify_in_portal: v })}
                  disabled={readOnly}
                  testId={`intake-notify-portal-${idx}`}
                />
              </div>
            )}

            {(s.kind === "vendor_engages" || s.kind === "service_rendering") && (
              <div className="space-y-2">
                {s.kind === "service_rendering" && (
                  <div className="grid sm:grid-cols-2 gap-2 mb-2">
                    <input
                      type="number"
                      min="0"
                      value={s.sla_hours || ""}
                      onChange={(e) => updateStage(idx, { sla_hours: e.target.value ? Number(e.target.value) : null })}
                      disabled={readOnly}
                      placeholder="SLA (hours) for completion"
                      className="text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-slate-900 disabled:bg-slate-50"
                    />
                    <input
                      value={s.delivery_notes || ""}
                      onChange={(e) => updateStage(idx, { delivery_notes: e.target.value })}
                      disabled={readOnly}
                      placeholder="Delivery notes (visible to admin)"
                      className="text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-slate-900 disabled:bg-slate-50"
                    />
                  </div>
                )}

                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                  Assets · {(s.assets || []).length}
                </div>
                {(s.assets || []).map((a, ai) => (
                  <div key={a.id} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3" data-testid={`intake-asset-${idx}-${ai}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <FileText className="w-4 h-4 text-slate-400 mt-1.5 shrink-0" />
                      <div className="grid sm:grid-cols-2 gap-2 flex-1">
                        <input
                          value={a.label}
                          onChange={(e) => updateAsset(idx, ai, { label: e.target.value })}
                          disabled={readOnly}
                          placeholder="Label (e.g. Intake form v2)"
                          className="text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 focus:border-slate-900 disabled:bg-slate-50 font-semibold"
                        />
                        <select
                          value={a.kind}
                          onChange={(e) => updateAsset(idx, ai, { kind: e.target.value })}
                          disabled={readOnly}
                          className="text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 focus:border-slate-900 disabled:bg-slate-50"
                        >
                          {ASSET_KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                        </select>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => removeAsset(idx, ai)}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                          title="Remove asset"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <textarea
                      value={a.description || ""}
                      onChange={(e) => updateAsset(idx, ai, { description: e.target.value })}
                      disabled={readOnly}
                      rows={2}
                      placeholder="Description / instructions"
                      className="w-full text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 mb-2 focus:border-slate-900 disabled:bg-slate-50"
                    />
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input
                        value={a.url || ""}
                        onChange={(e) => updateAsset(idx, ai, { url: e.target.value })}
                        disabled={readOnly}
                        placeholder="External URL (optional)"
                        className="text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 focus:border-slate-900 disabled:bg-slate-50"
                      />
                      <label className="inline-flex items-center gap-2 text-[12px] text-slate-700 px-3">
                        <input
                          type="checkbox"
                          checked={a.required}
                          onChange={(e) => updateAsset(idx, ai, { required: e.target.checked })}
                          disabled={readOnly}
                        />
                        Required
                      </label>
                    </div>
                  </div>
                ))}
                {!readOnly && (
                  <button
                    onClick={() => addAsset(idx)}
                    data-testid={`intake-add-asset-${idx}`}
                    className="w-full rounded-xl border border-dashed border-slate-300 bg-white py-2 text-xs font-semibold text-slate-600 hover:border-slate-900 hover:text-slate-900 inline-flex items-center justify-center gap-1.5"
                  >
                    <Plus className="w-3 h-3" /> Add asset
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Toggle({ icon: Icon, label, checked, onChange, disabled, testId }) {
  return (
    <label data-testid={testId} className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"} ${disabled ? "opacity-60 cursor-default" : ""}`}>
      <Icon className="w-4 h-4 text-slate-500" />
      <span className="text-sm font-semibold flex-1">{label}</span>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
    </label>
  );
}
